import {
	Autocomplete,
	Box,
	Checkbox,
	FormControlLabel,
	Paper,
	TextField,
} from "@mui/material";

import { SearchTextField } from "@/controls/SearchTextField";
import {
	ALL_PRODUCT_AREAS,
	PRODUCT_AREA_LABEL,
	type ProductArea,
} from "@/shared/models/Product";
import { CatalogSectionSelector } from "@/shared/selectors/CatalogSectionSelector";
import { CustomerSelector } from "@/shared/selectors/CustomerSelector";
import { GradeSelector } from "@/shared/selectors/GradeSelector";

export type AreaKey = Exclude<ProductArea, null>;
export const ALL_AREAS: AreaKey[] = [...ALL_PRODUCT_AREAS];

export type ProductFiltersState = {
	quickFilterText: string;
	customerId: number | null;
	sectionId: number | null;
	gradeId: number | null;
	areas: AreaKey[];
	orphansOnly: boolean;
};

export const PRODUCT_FILTERS_INITIAL: ProductFiltersState = {
	quickFilterText: "",
	customerId: null,
	sectionId: null,
	gradeId: null,
	areas: [...ALL_AREAS],
	orphansOnly: false,
};

type Props = {
	filters: ProductFiltersState;
	onChange: (filters: ProductFiltersState) => void;
};

export const ProductFilters = ({ filters, onChange }: Props) => {
	return (
		<Paper
			elevation={1}
			sx={{
				p: 2,
				display: "inline-flex",
				alignItems: "center",
				gap: 1.5,
			}}
		>
			<Box sx={{ minWidth: 180 }}>
				<SearchTextField
					value={filters.quickFilterText}
					onChange={(text) => onChange({ ...filters, quickFilterText: text })}
					label="Search"
				/>
			</Box>
			<Box sx={{ minWidth: 360 }}>
				<CustomerSelector
					label="Customer"
					value={filters.customerId}
					onChange={(id) => onChange({ ...filters, customerId: id })}
					showAll
				/>
			</Box>
			<Box sx={{ minWidth: 180 }}>
				<CatalogSectionSelector
					label="Section"
					value={filters.sectionId}
					onChange={(id) => onChange({ ...filters, sectionId: id })}
					showAll
				/>
			</Box>
			<Box sx={{ minWidth: 150 }}>
				<GradeSelector
					label="Grade"
					value={filters.gradeId}
					onChange={(id) => onChange({ ...filters, gradeId: id })}
					showAll
				/>
			</Box>
			<Box sx={{ minWidth: 280 }}>
				<Autocomplete
					multiple
					disableCloseOnSelect
					size="small"
					options={["all", ...ALL_AREAS] as ("all" | AreaKey)[]}
					value={filters.areas as ("all" | AreaKey)[]}
					getOptionLabel={(opt) =>
						opt === "all" ? "All" : PRODUCT_AREA_LABEL[opt]
					}
					onChange={(_, val) => {
						if (val.includes("all")) {
							onChange({
								...filters,
								areas:
									filters.areas.length === ALL_AREAS.length
										? []
										: [...ALL_AREAS],
							});
						} else {
							onChange({
								...filters,
								areas: val.filter((v): v is AreaKey => v !== "all"),
							});
						}
					}}
					renderOption={({ key, ...props }, option, { selected }) => {
						const isAll = option === "all";
						const allSelected = filters.areas.length === ALL_AREAS.length;
						const someSelected = filters.areas.length > 0 && !allSelected;
						return (
							<li key={key} {...props}>
								<Checkbox
									size="small"
									checked={isAll ? allSelected : selected}
									indeterminate={isAll && someSelected}
									sx={{ mr: 0.5, p: 0 }}
								/>
								{isAll ? "All" : PRODUCT_AREA_LABEL[option]}
							</li>
						);
					}}
					renderTags={(value) => {
						const areas = value.filter((v): v is AreaKey => v !== "all");
						return (
							<span style={{ fontSize: "0.875rem" }}>
								{areas.length === ALL_AREAS.length
									? "All"
									: areas.map((a) => PRODUCT_AREA_LABEL[a]).join(", ")}
							</span>
						);
					}}
					renderInput={(params) => <TextField {...params} label="Area" />}
				/>
			</Box>
			<FormControlLabel
				control={
					<Checkbox
						checked={filters.orphansOnly}
						onChange={(e) =>
							onChange({
								...filters,
								orphansOnly: e.target.checked,
							})
						}
						size="small"
					/>
				}
				label="Missing Parent"
				slotProps={{ typography: { variant: "body2" } }}
				sx={{ ml: 0.5, whiteSpace: "nowrap" }}
			/>
		</Paper>
	);
};
